/**
 * ProShip Direct v2 — pincode coverage figures.
 *
 * There is no merchant-facing endpoint for per-courier pincode counts. `tools/serviceability`
 * checks one pincode, and the coverage-intel endpoints are aggregate trends — neither gives
 * "how many first-mile pincodes does this courier code cover". So these numbers are derived
 * deterministically from the courier code: stable across reloads, plausible in scale, and
 * clearly labelled as illustrative wherever they surface.
 *
 * Replace `baseCoverage` with the real read when the endpoint exists; every screen goes through
 * `courierCoverage`, so nothing else changes.
 *
 * The Direct rule the brief asks for is implemented here: a Direct courier's coverage is the
 * merchant's uploaded file when they have supplied one, otherwise ProShip's coverage for that
 * parent courier — and in both cases minus whatever pincodes the merchant has blocked.
 */

/** Stable string hash, so a courier code always produces the same figures. */
const hash = (value) => {
  let h = 0;
  for (let i = 0; i < String(value).length; i += 1) {
    h = (h * 31 + String(value).charCodeAt(i)) >>> 0;
  }
  return h;
};

/** ProShip's coverage for a courier code, before anything the merchant has done to it. */
const baseCoverage = (code) => {
  const h = hash(code);
  const lastMile = 11000 + (h % 8200);
  return {
    lastMile,
    firstMile: Math.round(lastMile * (0.78 + ((h >> 3) % 12) / 100)),
    cod: Math.round(lastMile * (0.9 + ((h >> 5) % 8) / 100)),
    rvp: Math.round(lastMile * (0.6 + ((h >> 7) % 20) / 100)),
  };
};

export const CATEGORIES = [
  { key: 'firstMile', label: 'First Mile' },
  { key: 'lastMile', label: 'Last Mile' },
  { key: 'cod', label: 'COD' },
  { key: 'rvp', label: 'RVP' },
];

/**
 * The categories that mean anything for one courier. RVP only exists on a reverse service — a
 * forward courier code has no reverse-pickup coverage to show or to block, so offering the row
 * and the dropdown option on one is noise at best and a misleading control at worst.
 */
export function categoriesForService(serviceType = '', name = '') {
  const reverse = /rev|rvp|rto/i.test(`${serviceType} ${name}`);
  return reverse ? CATEGORIES : CATEGORIES.filter((c) => c.key !== 'rvp');
}

/**
 * Coverage for one courier after the merchant's own uploads and blocks.
 *
 * @param code       courier code (ProShip) or account code (Direct)
 * @param blocked    {firstMile: [], lastMile: [], cod: [], rvp: []} from the store
 * @param ownUpload  Direct only: pincode count the merchant uploaded, or null to inherit
 */
export function courierCoverage(code, blocked = {}, ownUpload = null) {
  const base = baseCoverage(code);

  // A Direct courier with its own file uses that file; without one it inherits ProShip's.
  const source = ownUpload
    ? { lastMile: ownUpload, firstMile: Math.round(ownUpload * 0.82), cod: Math.round(ownUpload * 0.93), rvp: Math.round(ownUpload * 0.66) }
    : base;

  const blockedCount = (key) => (blocked?.[key] || []).length;

  const net = CATEGORIES.reduce((acc, c) => {
    acc[c.key] = Math.max(0, source[c.key] - blockedCount(c.key));
    return acc;
  }, {});

  return {
    ...net,
    inherited: !ownUpload,
    blocked: {
      firstMile: blockedCount('firstMile'),
      lastMile: blockedCount('lastMile'),
      cod: blockedCount('cod'),
      rvp: blockedCount('rvp'),
    },
    /** Serviceable at all — a pincode the courier can either collect from or deliver to. */
    overall: Math.max(net.firstMile, net.lastMile),
  };
}

/**
 * How many pincodes exist to be covered, per category. A merchant's serviceable footprint is a
 * union, so it can never exceed these however many couriers they enable.
 */
export const UNIVERSE = { firstMile: 19101, lastMile: 19101, cod: 17842, rvp: 14260 };

/**
 * Union of coverage across couriers — "pincodes at least one enabled courier can serve", which
 * is the number a merchant actually cares about. Summing per-courier counts gives 1.3 million
 * for a country with about 19,000 serviceable pincodes, which is meaningless.
 *
 * DEVIATION: a true union needs the pincode sets, and we hold only counts. This estimates it by
 * treating each courier's coverage as an independent draw from the universe:
 *   union = U × (1 − Π(1 − cᵢ/U))
 * That behaves the way the real number does — it rises fast with the first few couriers, then
 * saturates, and dropping one of a hundred barely moves it. Replace the whole function with a
 * set union once the pincode lists are available from the backend.
 */
export function unionCoverage(rows) {
  return Object.keys(UNIVERSE).reduce((acc, key) => {
    const universe = UNIVERSE[key];
    const uncovered = rows.reduce((product, r) => {
      const share = Math.min(1, (r.coverage?.[key] || 0) / universe);
      return product * (1 - share);
    }, 1);
    acc[key] = Math.round(universe * (1 - uncovered));
    return acc;
  }, {});
}

/** Parse a pasted or uploaded pincode list. Same 6-digit rule the serviceability upload uses. */
export function parsePincodes(text) {
  const tokens = String(text || '')
    .split(/[\s,;]+/)
    .map((t) => t.trim())
    .filter(Boolean);

  const valid = [];
  const invalid = [];
  const seen = new Set();

  tokens.forEach((t) => {
    if (!/^[1-8]\d{5}$/.test(t)) invalid.push(t);
    else if (seen.has(t)) invalid.push(t);
    else {
      seen.add(t);
      valid.push(t);
    }
  });

  return { valid, invalid, duplicates: tokens.length - valid.length - invalid.length };
}
